import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { createProductThunk, updateProductThunk } from "../../redux/slices/productSlice";
import { fetchSuppliers } from "../../redux/slices/supplierSlice";

const EMPTY = {
  itemName: "", itemCategory: "Medicine", unit: "", purchaseRate: "",
  sellingRate: "", minimumStockLevel: "", defaultSupplierID: "", status: "Active",
};

const categoryOptions = [
  { value: "Medicine", label: "Medicine" },
  { value: "Consumable", label: "Consumable" },
];
const statusOptions = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const menuFixed = { menuPosition: "fixed", styles: { menu: (b) => ({ ...b, zIndex: 9999 }) } };

const ProductModal = ({ show, onClose, editRecord }) => {
  const dispatch = useDispatch();
  const suppliers = useSelector((s) => s.suppliers.list);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // Use supplierID as the primary key (API returns supplierID, not id)
  const supplierOptions = suppliers.map((s) => ({
    value: s.supplierID || s.id,
    label: s.supplierName,
  }));

  useEffect(() => { dispatch(fetchSuppliers()); }, [dispatch]);

  useEffect(() => {
    setForm(editRecord ? { ...EMPTY, ...editRecord } : EMPTY);
  }, [editRecord, show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setSelect = (field) => (opt) => setForm((f) => ({ ...f, [field]: opt?.value ?? "" }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.itemName.trim()) {
      Swal.fire("Required", "Item Name is required.", "warning"); return;
    }
    setSaving(true);
    try {
      if (editRecord?.id || editRecord?.productID) {
        await dispatch(updateProductThunk({ id: editRecord.id || editRecord.productID, data: form })).unwrap();
      } else {
        await dispatch(createProductThunk(form)).unwrap();
      }
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Product ${editRecord ? "updated" : "added"} successfully`, showConfirmButton: false, timer: 2000 });
      onClose();
    } catch {
      Swal.fire("Error", "Failed to save product.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{editRecord ? "Edit Product" : "Add Product"}</h4>
              </div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-8 mb-3">
                    <label className="form-label">Item Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.itemName} onChange={set("itemName")} placeholder="Product / Item Name" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Category</label>
                    <Select classNamePrefix="react-select" options={categoryOptions}
                      value={categoryOptions.find((o) => o.value === form.itemCategory) || null}
                      onChange={setSelect("itemCategory")} {...menuFixed} />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Unit</label>
                    <input className="form-control" value={form.unit} onChange={set("unit")} placeholder="Nos / ml / Box" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Purchase Rate (₹)</label>
                    <input type="number" className="form-control" value={form.purchaseRate} onChange={set("purchaseRate")} placeholder="0.00" min="0" step="0.01" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Selling Rate (₹)</label>
                    <input type="number" className="form-control" value={form.sellingRate} onChange={set("sellingRate")} placeholder="0.00" min="0" step="0.01" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Min Stock Level</label>
                    <input type="number" className="form-control" value={form.minimumStockLevel} onChange={set("minimumStockLevel")} placeholder="0" min="0" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Default Supplier</label>
                    <Select classNamePrefix="react-select" options={supplierOptions}
                      value={supplierOptions.find((o) => o.value === form.defaultSupplierID) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, defaultSupplierID: opt ? opt.value : "" }))}
                      placeholder="Select Supplier" isClearable {...menuFixed} />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Status</label>
                    <Select classNamePrefix="react-select" options={statusOptions}
                      value={statusOptions.find((o) => o.value === form.status) || null}
                      onChange={setSelect("status")} {...menuFixed} />
                  </div>
                </div>
                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : editRecord ? "Save Changes" : "Add Product"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default ProductModal;
